import { AutoAwesome } from '@mui/icons-material';
import { useRegistrationImagesCompletenessGate } from '~/hooks/image/useImageStreamProgress';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useSetRecoilState } from 'recoil';
import RegistrationToolShortcuts from '~/components/toolbar/steps/RegistrationToolShortcuts';
import ToolGroup from '~/components/toolbar/shared/ToolGroup';
import ToolPropertiesTray from '~/components/toolbar/shared/ToolPropertiesTray';
import { ToolRailPortal } from '~/components/toolbar/shared/ToolRailSlots';
import { useActiveFusionTarget } from '~/hooks/target/useActiveFusionTarget';
import { useFusedPairDrawnApart } from '~/hooks/environment/useEnvironmentViews';
import AutoRegisterTool from '~/components/toolbar/tools/AutoRegisterTool';
import AutoRegisterOptions from '~/components/toolbar/tools/AutoRegisterTool/AutoRegisterOptions';
import BoxZoomTool from '~/components/toolbar/tools/BoxZoomTool';
import MeasureTool from '~/components/toolbar/tools/MeasureTool';
import WindowingHistogramTool from '~/components/toolbar/tools/WindowingHistogramTool';
import SeriesOverviewTool from '~/components/toolbar/tools/SeriesOverviewTool';
import PanTool from '~/components/toolbar/tools/PanTool';
import RegistrationInvertTool from '~/components/toolbar/tools/RegistrationInvertTool';
import RegistrationModeTool from '~/components/toolbar/tools/RegistrationModeTool';
import RegistrationUndoRedoTool from '~/components/toolbar/tools/RegistrationUndoRedoTool';
import ResetViewTool from '~/components/toolbar/tools/ResetViewTool';
import RotateViewTool from '~/components/toolbar/tools/RotateViewTool';
import ScreenshotTool from '~/components/toolbar/tools/ScreenshotTool';
import { RegistrationMode } from '~/engine/tool/tools/RegistrationTool';
import { useRegistrationState } from '~/hooks/registration/useRegistrationStore';
import { useDropRegistrationVoi } from '~/hooks/registration/useDropRegistrationVoi';
import {
    autoRegistrationInProgressState,
    registrationModeState,
    selectedToolState,
    voiCheckboxState,
} from '~/state/atoms/toolsAtoms';

export default function RegistrationTools() {
    const intl = useIntl();
    const registrationState = useRegistrationState();
    const [tool, setTool] = useRecoilState(selectedToolState);
    const [, setRegistrationMode] = useRecoilState(registrationModeState);
    const [isAutoRegisterOpen, setIsAutoRegisterOpen] = useState(false);
    const setVoiCheckbox = useSetRecoilState(voiCheckboxState);
    const [isAutoRegistering, setAutoRegistering] = useRecoilState(
        autoRegistrationInProgressState
    );
    // Registration reads BOTH volumes: elastix takes the fixed and the moving
    // image's Int16 buffers, and the backend task ships both series as DICOM.
    // Either one being partial makes the fitted transform wrong, and a manual
    // alignment is verified against whatever slices happen to be on screen.
    const imageGate = useRegistrationImagesCompletenessGate();

    // The pair the tools act on is the primary row's own fusion.
    const fusion = useActiveFusionTarget();
    const hasSecondaryImage = fusion.actionable;
    const isApproved =
        registrationState.selectedRegistration?.isApproved ?? false;
    // Blocks every tool that acts on the pair; the invert control gates on
    // the displayed registration itself, since it also inverts one a drag
    // from the primary cannot edit.
    const isBusy = isAutoRegistering || imageGate.isBlocked;
    const isDisabled = !hasSecondaryImage || isApproved || isBusy;
    // Every change to the registration made without a confirmation is judged
    // on the blend: with the pair drawn apart no tile shows it against the
    // primary. Invert asks for a confirmation and stays available.
    const isDrawnApart = useFusedPairDrawnApart(fusion.environment);
    const isAlignmentDisabled = isDisabled || isDrawnApart;
    // Named only while nothing is fused: a fused pair the tools cannot act
    // on is disabled for another reason, which this text would misstate.
    const disabledReason =
        fusion.secondaryImage === undefined
            ? intl.formatMessage({ id: 'registration.selectImageHint' })
            : imageGate.reason;
    // The blend is named only while it is the one reason: blending the
    // pair must enable the tools the text names.
    const alignmentDisabledReason =
        !isDisabled && isDrawnApart
            ? intl.formatMessage({ id: 'registration.blendRequiredHint' })
            : disabledReason;
    // A mode refused by its key or the radial menu says why, so the modes
    // also name an approved registration, and a running auto-registration
    // where the text above names nothing.
    let modeDisabledReason = alignmentDisabledReason;
    if (fusion.secondaryImage !== undefined && isApproved) {
        modeDisabledReason = intl.formatMessage({
            id: 'registration.approved',
        });
    } else if (disabledReason === undefined && isAutoRegistering) {
        modeDisabledReason = intl.formatMessage({
            id: 'registration.auto.running',
        });
    }

    // Closes the auto-register tray and its VOI box; a VOI mode left behind
    // would arm nothing, so it returns to translation.
    const closeAutoRegister = useCallback(() => {
        setIsAutoRegisterOpen(false);
        setVoiCheckbox(false);
        setRegistrationMode((prev) =>
            prev.mode === RegistrationMode.VOI
                ? { ...prev, mode: RegistrationMode.TRANSLATION }
                : prev
        );
    }, [setVoiCheckbox, setRegistrationMode]);

    // Arms the registration tool while an editable pair is on show and
    // returns to pan when it is approved or goes away, so no drag tool stays
    // armed with nothing to act on.
    useEffect(() => {
        if (!hasSecondaryImage) {
            setTool((current) =>
                current === 'registration' ? 'pan' : current
            );
            return;
        }
        if (isApproved) {
            setTool('pan');
            closeAutoRegister();
        } else {
            setTool('registration');
            setRegistrationMode((prev) => ({
                ...prev,
                mode: prev.mode ?? RegistrationMode.TRANSLATION,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasSecondaryImage, isApproved]);

    // With the pair drawn apart an armed alignment returns to pan, also
    // after the arming effect above arms it again. A run in progress keeps
    // its tray, which holds its Cancel, and returns to pan when it ends.
    // Drawing the pair blended again arms nothing: the user may have chosen
    // another tool.
    useEffect(() => {
        if (!isDrawnApart || isAutoRegistering) {
            return;
        }
        setTool((current) => (current === 'registration' ? 'pan' : current));
        closeAutoRegister();
    }, [
        isDrawnApart,
        isAutoRegistering,
        hasSecondaryImage,
        isApproved,
        setTool,
        closeAutoRegister,
    ]);

    // A registration box is seeded from the fused image, so it belongs to
    // one pair: a change of pair drops it, along with the tray that showed
    // it, and cancels a run in progress, whose result was for the old pair.
    const dropRegistrationVoi = useDropRegistrationVoi();
    const pairKey = `${fusion.seriesUid}|${fusion.secondaryImage?.seriesUid}`;
    useEffect(() => {
        closeAutoRegister();
        dropRegistrationVoi();
        setAutoRegistering(false);
    }, [pairKey, closeAutoRegister, dropRegistrationVoi, setAutoRegistering]);

    return (
        <>
            <ToolGroup>
                <PanTool />
                <BoxZoomTool />
                <RotateViewTool />
                <ResetViewTool />
                <WindowingHistogramTool />
                <SeriesOverviewTool />
                <MeasureTool />
                <ScreenshotTool />
            </ToolGroup>
            <ToolRailPortal group="tools">
                <RegistrationModeTool
                    isDisabled={isAlignmentDisabled}
                    disabledReason={modeDisabledReason}
                    onModeSelected={closeAutoRegister}
                />
                <AutoRegisterTool
                    isDisabled={isAlignmentDisabled}
                    disabledReason={alignmentDisabledReason}
                    isOpen={isAutoRegisterOpen && tool === 'registration'}
                    onToggle={() => {
                        const opening =
                            !isAutoRegisterOpen || tool !== 'registration';
                        setIsAutoRegisterOpen(opening);
                        setVoiCheckbox(opening);
                        if (opening) {
                            setRegistrationMode((m) => ({
                                ...m,
                                mode: RegistrationMode.VOI,
                            }));
                            setTool('registration');
                        }
                    }}
                />
                <RegistrationInvertTool isDisabled={isBusy} />
            </ToolRailPortal>
            <ToolGroup>
                <RegistrationUndoRedoTool
                    isDisabled={isAlignmentDisabled}
                    disabledReason={alignmentDisabledReason}
                />
            </ToolGroup>
            <RegistrationToolShortcuts
                isDisabled={isAlignmentDisabled}
                disabledReason={modeDisabledReason}
                onModeSelected={closeAutoRegister}
            />
            {isAutoRegisterOpen && tool === 'registration' && (
                <ToolPropertiesTray
                    title={intl.formatMessage({
                        id: 'workflow.imageRegistration.autoRegister',
                    })}
                    icon={<AutoAwesome />}
                    // Run follows the VOI box placed on the views.
                    collapsesOnViewPress={false}
                    // Shift highlights Rotation too while it is held.
                    anchor="auto-register"
                >
                    <AutoRegisterOptions
                        isDisabled={isDisabled}
                        disabledReason={disabledReason}
                    />
                </ToolPropertiesTray>
            )}
        </>
    );
}
